import { getStoredRealm } from "@/utils/auth-storage";
import { Box } from "@mui/material";
import { GridColDef, GridSortItem } from "@mui/x-data-grid";
import { useFormik } from "formik";
import { useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import CustomDataGridV2 from "../../components/CustomDataGridV2";
import Flex from "../../components/Flex";
import Text from "../../components/Text";
import ActionMenu from "../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../components/app/DeleteModal";
import {
  ADD_NEW_PATIENT_INITIAL_VALUES,
  ADD_NEW_PATIENT_VALIDATION_SCHEMA,
} from "../../constants/provider/patient/add-new-patient/add-new-patient-form";
import useDisclosure from "../../hooks/use-disclosure";
import { useQueryParams } from "../../hooks/use-query-param";
import { usePatientsList } from "../../queries/use-get-patients";
import { Patient } from "../../services";
import { getViewAs } from "../../utils/auth-storage";
import { formatDate } from "../../utils/date-time-utility";
import {
  DEFAULT_PAGE_SIZE,
  DGT_Pagination,
  getAge,
  getFormattedId,
} from "../../utils/utils";
const GENDER_RECORD = {
  MALE: "male",
  FEMALE: "female",
  OTHER: "other",
} as Record<string, string>;

const ProviderGroupPatients = () => {
  const params = useParams();

  const { searchKey }: { searchKey: string; stateFilter: string } =
    useOutletContext();

  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const realm = getStoredRealm();
  const viewAs = getViewAs();

  const {
    data: ProviderGroupPatientsList,
    isLoading: tableDataLoading,
    isFetching,
  } = usePatientsList({
    gridSorting,
    pagination,
    searchKey,
    providerGroupId:
      params?.providerGroupId ?? (viewAs === "provider" ? realm : undefined),
  });

  const COLUMNS: GridColDef<Patient>[] = [
    {
      field: "uuid",
      flex: 0.5,
      disableColumnMenu: true,
      headerName: "Patient ID",
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Text isTabletext>{getFormattedId(row?.ecarehealthPatientId)}</Text>
        );
      },
    },
    {
      field: "name",
      flex: 0.8,
      minWidth: 200,
      disableColumnMenu: true,
      headerName: "Patient Name",
      renderCell: ({ row }) => {
        return (
          <Flex gap={"20px"}>
            <Flex gap={0.5}>
              <Text isTabletext marginBottom={"3px"}>
                {`${row?.firstName} ${row?.lastName} `}
              </Text>
              <Text
                isTabletext
                variant="subtitle2"
              >{`(${getAge(row?.birthDate)} yrs-${
                GENDER_RECORD[row.gender]
              })`}</Text>
            </Flex>
          </Flex>
        );
      },
      // todo
      // temparory sort false because B.E not accepting key giving 500
      sortable: false,
    },
    {
      field: "birthDate",
      flex: 0.5,
      minWidth: 120,
      disableColumnMenu: true,
      headerName: "Date Of Birth",
      sortable: false,
      renderCell: ({ row }) => {
        return <Text isTabletext>{formatDate(row?.birthDate)}</Text>;
      },
    },
    {
      field: "mobileNumber",
      flex: 0.6,
      minWidth: 125,
      disableColumnMenu: true,
      headerName: "Contact",
      sortable: false,
    },
    {
      field: "email",
      flex: 1,
      minWidth: 250,
      disableColumnMenu: true,
      headerName: "Email",
      sortable: false,
    },
    {
      field: "providerId",
      flex: 0.7,
      minWidth: 150,
      disableColumnMenu: true,
      headerName: "Primary Care Provider",
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => {
        const values = Object.values(row?.providerId ?? {});
        return (
          <Text isTabletext>{values[0] ? (values[0] as string) : "-"}</Text>
        );
      },
      sortable: false,
    },
    // Location column not required for now — pending B.E. key (ECRH-3897)
    // {
    //   field: "locationId",
    //   flex: 0.9,
    //   minWidth: 150,
    //   headerAlign: "center",
    //   disableColumnMenu: true,
    //   headerName: "Location",
    //   sortable: false,
    //   align: "center",
    //   renderCell: ({ row }) => {
    //     const values = Object.values(row?.locationId ?? {});
    //     return (
    //       <Text isTabletext>{values[0] ? (values[0] as string) : "-"}</Text>
    //     );
    //   },
    // },
    {
      field: "lastAppointment",
      flex: 0.8,
      minWidth: 140,
      disableColumnMenu: true,
      headerName: "Last Visit",
      sortable: false,
      renderCell: ({ value }) => (
        <Text isTabletext>{value ? formatDate(value) : "-"}</Text>
      ),
    },
    {
      field: "active",
      headerAlign: "center",
      width: 150,
      disableColumnMenu: true,
      headerName: "Status",
      sortable: false,
      align: "center",
      renderCell: ({ value }) => <Text>{value ? "Active" : "InActive"}</Text>,
    },
    {
      field: "Action",
      width: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <ActionMenu
          options={[
            { value: "edit", label: "Edit" },
            row?.active
              ? { value: "inactive", label: "Inactive" }
              : { value: "active", label: "Active" },
          ]}
          onActionClick={(v) => {
            if (v === "edit") patientFormDisclosure.open();
            if (v === "active" || v === "inactive")
              deleteDisClosure.open(row?.uuid);
          }}
        />
      ),
    },
  ];
  const isAction = useQueryParams("isAction");
  const patientFormDisclosure = useDisclosure(false);
  const deleteDisClosure = useDeleteDisClosure(false);

  const formik = useFormik({
    initialValues: ADD_NEW_PATIENT_INITIAL_VALUES,
    validationSchema: ADD_NEW_PATIENT_VALIDATION_SCHEMA,
    onSubmit,
  });

  function onSubmit() {
    formik.resetForm();
  }

  return (
    <Box width={"100%"}>
      {/* edit action demographics form */}
      {/* FUTURE reference
       <UpsertPatientForm
        opened={patientFormDisclosure.opened}
        close={patientFormDisclosure.close}
        prefillValues={EDIT_NEW_PATIENT_INITIAL_VALUES}
      /> */}

      <DeleteModal
        w="24em"
        content={"You want to change the status"}
        open={deleteDisClosure.opened}
        onClose={deleteDisClosure?.close}
        id={deleteDisClosure?.deleteId}
        onConfirmation={() => {
          deleteDisClosure.close();
          return new Promise(() => {});
        }}
        iconColor="primary.main"
      />

      <Box height={"calc(100vh - 22rem)"}>
        <CustomDataGridV2
          loading={tableDataLoading || isFetching}
          searchString={searchKey}
          getRowId={(row) => row.uuid}
          rows={ProviderGroupPatientsList?.data?.content ?? []}
          rowCount={ProviderGroupPatientsList?.data?.page?.totalElements}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          paginationModel={pagination}
          columns={
            isAction === "true"
              ? COLUMNS
              : COLUMNS?.filter((it) => it.field !== "Action")
          }
          disableRowSelectionOnClick
        />
      </Box>
    </Box>
  );
};

export default ProviderGroupPatients;
